import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Badge, Box, Container, Flex, Heading, Separator } from "@chakra-ui/react";
import { SegmentedControl } from "./components/ui/segmented-control";
import { Button } from "./components/ui/button";
import { HiClipboardCopy, HiDownload, HiCheck } from "react-icons/hi";
import {
  ApiCheckResponse,
  ApiSpecResponse,
  Issue,
  IssueSeverity,
  Observation,
} from "./types";
import { Table as SummaryTable } from "./Summary";
import { IssueList } from "./Issues";
import { ObservationList } from "./Observation";
import { EntityView } from "./EntityView";
import { Provider } from "./components/ui/provider";
import Header from "./Header";
import PreviewBanner from "./PreviewBanner";
import Footer from "./Footer";
import JsonUploader from "./JsonUploader";
import Loading from "./Loading";
import ErrorPanel from "./Error";
import PassOrFail from "./PassOrFail";
import Synopsis from "./Synopsis";

const base_url = import.meta.env.DEV ? "http://127.0.0.1:8000" : "";

const SEVERITY_BADGE: Record<
  IssueSeverity,
  { label: string; color: string }
> = {
  error: { label: "Error", color: "red" },
  warning5: { label: "Needs Your Attention", color: "orange" },
  warning1: { label: "Conditionally Passed", color: "yellow" },
  observation: { label: "Observation", color: "gray" },
};

// Shared layout constants. The 80%-wide-with-margins box wraps every
// top-level section of the page.
const SECTION_STYLE: React.CSSProperties = {
  width: "80%",
  margin: "0% 5% 2% 5%",
};

const App: React.FC = () => (
  <Provider>
    <OMCChecker />
  </Provider>
);

type DataState = "none" | "loading" | "error" | "loaded";
type View = "byRule" | "byEntity";

const OMCChecker: React.FC = () => {
  const [spec, setSpec] = useState<ApiSpecResponse | null>(null);
  const [specState, setSpecState] = useState<"loading" | "error" | "loaded">(
    "loading"
  );
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<ApiCheckResponse | null>(null);
  const [dataState, setDataState] = useState<DataState>("none");
  const [error, setError] = useState<{ error: string; message: string } | null>(
    null
  );
  const [selectedRule, setSelectedRule] = useState<string | null>(null);
  const [view, setView] = useState<View>("byRule");
  const [copied, setCopied] = useState(false);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  // Pretty-printed copy of the raw /api/check response, for copy/download.
  const responseJson = useMemo(
    () => (data ? JSON.stringify(data, null, 2) : ""),
    [data]
  );

  const handleCopyResponse = useCallback(async () => {
    if (!responseJson) return;
    try {
      await navigator.clipboard.writeText(responseJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Clipboard copy failed:", e);
    }
  }, [responseJson]);

  const handleDownloadResponse = useCallback(() => {
    if (!responseJson) return;
    const blob = new Blob([responseJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const base = file?.name?.replace(/\.json$/i, "") || "omc";
    a.href = url;
    a.download = `${base}-validation.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [responseJson, file]);

  // Fetch spec once on mount.
  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get<ApiSpecResponse>(
          `${base_url}/api/spec`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-Requested-By": "UI",
            },
          }
        );
        setSpec(response.data);
        setSpecState("loaded");
      } catch (e) {
        console.error("Error fetching spec:", e);
        setSpecState("error");
      }
    })();
  }, []);

  const handleFileUpload = useCallback(async () => {
    if (!file) return;
    setSelectedRule(null);
    setError(null);
    setData(null);
    setDataState("loading");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post<ApiCheckResponse>(
        `${base_url}/api/check`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-Requested-By": "UI",
          },
        }
      );
      setData(response.data);
      setDataState("loaded");
    } catch (e) {
      console.error("Received unexpected error:", e);
      const errorObject = {
        error: "Unable to process file",
        message: "If the problem persists, please contact MovieLabs.",
      };
      if (axios.isAxiosError(e)) {
        const errorCode = e.response?.status
          ? `Error code ${e.response?.status}. `
          : "";
        if (e.response?.data?.error && e.response?.data?.message) {
          errorObject.error = `${errorCode}${e.response.data.error}`;
          errorObject.message = e.response.data.message;
        } else {
          errorObject.error = `${errorCode}Unable to process file`;
        }
      }
      setError(errorObject);
      setDataState("error");
    }
  }, [file]);

  useEffect(() => {
    if (file) handleFileUpload();
  }, [file, handleFileUpload]);

  useEffect(() => {
    if (selectedRule && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedRule]);

  const issuesForRule: Issue[] = useMemo(() => {
    if (!data || !selectedRule) return [];
    const ids = data.issuesByRule[selectedRule] ?? [];
    const set = new Set(ids);
    return data.issues.filter((i) => set.has(i.id));
  }, [data, selectedRule]);

  const observationsForRule: Observation[] = useMemo(() => {
    if (!data || !selectedRule) return [];
    const ids = data.observationsByRule[selectedRule] ?? [];
    const set = new Set(ids);
    return data.observations.filter((o) => set.has(o.id));
  }, [data, selectedRule]);

  const selectedRuleSeverity: IssueSeverity | null = useMemo(() => {
    if (!data || !selectedRule) return null;
    return data.summary[selectedRule]?.severity ?? null;
  }, [data, selectedRule]);

  const { hasErrors, hasWarnings } = useMemo(() => {
    if (!data) return { hasErrors: false, hasWarnings: false };
    let err = false;
    let warn = false;
    for (const entry of Object.values(data.summary)) {
      if (entry.status !== "failed") continue;
      if (entry.severity === "error") err = true;
      else if (entry.severity === "warning5" || entry.severity === "warning1") {
        warn = true;
      }
    }
    return { hasErrors: err, hasWarnings: warn };
  }, [data]);

  const isObservation = selectedRuleSeverity === "observation";
  const itemCount = isObservation
    ? observationsForRule.length
    : issuesForRule.length;
  const sevBadge = selectedRuleSeverity
    ? SEVERITY_BADGE[selectedRuleSeverity]
    : null;
  const sevLabel = sevBadge
    ? isObservation
      ? "Observation"
      : `${itemCount} ${itemCount === 1 ? "Issue" : "Issues"} — ${sevBadge.label}`
    : "";

  return (
    <Flex direction="column" minHeight="100vh">
      <Header />
      <PreviewBanner />

      <Box as="main" flex="1">
        <div style={SECTION_STYLE}>
          <Container>
            <Synopsis schemas={spec?.schemas} />
          </Container>
        </div>

        <div style={SECTION_STYLE}>
          <Container>
            <Heading size="2xl" padding="20px 0 20px 0">
              OMC JSON
            </Heading>
            <JsonUploader setFile={setFile} specState={specState} />
          </Container>
        </div>

        <Separator />

        {(dataState !== "none" || data) && (
          <div style={SECTION_STYLE}>
            <Container>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                padding="20px 0 20px 0"
                gap="10px"
                wrap="wrap"
              >
                <Heading size="2xl">Validation Results</Heading>
                {data && (
                  <Flex gap="10px">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyResponse}
                    >
                      {copied ? <HiCheck /> : <HiClipboardCopy />}
                      {copied ? "Copied" : "Copy Results (JSON)"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleDownloadResponse}
                    >
                      <HiDownload />
                      Download Results (JSON)
                    </Button>
                  </Flex>
                )}
              </Flex>
            </Container>
            {dataState === "loading" && <Loading />}
            {error && <ErrorPanel error={error.error} message={error.message} />}
            {data && (
              <PassOrFail
                hasErrors={hasErrors}
                hasWarnings={hasWarnings}
                data={data}
              />
            )}
            {data && spec && (
              <Container>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  padding="10px 0"
                >
                  <Heading size="xl">Summary</Heading>
                  <SegmentedControl
                    value={view}
                    onValueChange={(d) => d.value && setView(d.value as View)}
                    items={[
                      { value: "byRule", label: "By rule" },
                      { value: "byEntity", label: "By entity" },
                    ]}
                  />
                </Flex>
                {view === "byRule" ? (
                  <SummaryTable
                    summary={data.summary}
                    rules={spec.rules}
                    onRowClick={setSelectedRule}
                  />
                ) : (
                  <EntityView
                    issues={data.issues}
                    issuesByEntity={data.issuesByEntity}
                  />
                )}
                <div style={{ paddingBottom: "40px" }} />
              </Container>
            )}
            {data && view === "byRule" && selectedRule && (
              <Container ref={resultsRef}>
                <Heading size="xl" padding="10px 0 10px 0">
                  Rule {selectedRule} Results
                </Heading>
                {sevBadge && (
                  <Heading size="md" padding="0 0 20px 0">
                    <Badge colorPalette={sevBadge.color}>{sevLabel}</Badge>
                  </Heading>
                )}
                {isObservation ? (
                  <ObservationList observations={observationsForRule} />
                ) : (
                  <IssueList issues={issuesForRule} />
                )}
                <div style={{ paddingBottom: "40px" }} />
              </Container>
            )}
          </div>
        )}
      </Box>

      <Footer version={spec?.applicationVersion} />
    </Flex>
  );
};

export default App;
